import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Tooltip } from "react-tippy";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import JsBarcode from "jsbarcode";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import AddSingleDialog from "./AddSingleDialog";
import styles from "./main.module.scss";
import { handlePostRequest } from "../../../services/PostTemplate";
import { handleGetRequest } from "../../../services/GetTemplate";
import { handlePutRequest } from "../../../services/PutTemplate";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";

const PendingPackages = () => {
    const [pendingPackages, setPendingPackages] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [editingPackage, setEditingPackage] = useState(null);
    const dispatch = useDispatch();

    const userRole = localStorage.getItem("role");
    const isClerk = userRole === "clerk";
    const userId = localStorage.getItem("userId");
    const userName = localStorage.getItem("user");

    const fetchPendingPackages = async () => {
        try {
            setIsLoading(true);
            let endpoint = "";

            if (userRole === "admin" || userRole === "clerk") {
                endpoint = "/getAllPendingPackages";
            } else if (userRole === "courier") {
                endpoint = `/getPendingPackageByCourier/${userId}`;
            } else {
                endpoint = `/clientPendingPackage/${userId}`;
            }

            const response = await handleGetRequest(endpoint);
            setPendingPackages(response?.data || []);
        } catch (error) {
            console.error("Error fetching packages:", error);
            toast.error("Failed to load pending packages");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPendingPackages();
        setIsAdmin(userRole === "admin");
    }, [userRole]);

    const handleFileUpload = async (file) => {
        const newPackages = [];

        try {
            if (file.name.endsWith(".csv")) {
                await new Promise((resolve) => {
                    Papa.parse(file, {
                        header: true,
                        skipEmptyLines: true,
                        complete: (results) => {
                            results.data.forEach((row) => {
                                newPackages.push(createPackageObject(row));
                            });
                            resolve();
                        },
                    });
                });
            } else if (file.name.endsWith(".xlsx")) {
                const data = await file.arrayBuffer();
                const workbook = XLSX.read(data);
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                jsonData.forEach((row) => {
                    newPackages.push(createPackageObject(row));
                });
            } else {
                toast.error("Unsupported file format. Please upload CSV or XLSX.");
                return;
            }

            if (newPackages.length > 0) {
                await handleBulkUpload(newPackages);
            }
        } catch (error) {
            console.error("File processing error:", error);
            toast.error("Error processing file");
        }
    };

    const createPackageObject = (row) => ({
        trackingNumber: row["Tracking Number"] || "",
        packageName: row["Package Name"] || "",
        description: row["Description"] || "",
        quantity: parseInt(row["Quantity"]) || 1,
        courier: isAdmin ? "" : userId,
        courierName: isAdmin ? "" : userName,
        billTo: isAdmin ? "" : userId,
        billToName: isAdmin ? "" : userName,
        consigneeName: "",
    });

    const handleBulkUpload = async (packages) => {
        try {
            setIsLoading(true);
            const response = await dispatch(handlePostRequest({ packages }, "/createPendingPackagesfromCSV", true, true));

            if (response !== "error") {
                toast.success("Packages uploaded successfully");
                fetchPendingPackages();
            }
        } catch (error) {
            console.error("Bulk upload error:", error);
            toast.error("Failed to upload packages");
        } finally {
            setIsLoading(false);
        }
    };

    const handleUploadCSV = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".csv, .xlsx";
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (file) handleFileUpload(file);
        };
        input.click();
    };

    const handlePackageSubmit = async (values, isEdit) => {
        try {
            setIsLoading(true);
            setIsDialogOpen(false);

            if (isEdit) {
                const result = await handlePutRequest(values, "/updatePendingPackage");
                if (result?.success) {
                    toast.success("Package updated successfully");
                    fetchPendingPackages();
                }
            } else {
                const result = await dispatch(handlePostRequest(values, "/createSinglePendingPackage", true, true));
                if (result !== "error") {
                    toast.success("Package added successfully");
                    fetchPendingPackages();
                }
            }
        } catch (error) {
            console.error("Package submission error:", error);
            toast.error(`Error ${isEdit ? "updating" : "adding"} package`);
        } finally {
            setIsLoading(false);
            setEditingPackage(null);
        }
    };

    const handleDeletePackage = async (packageData) => {
        try {
            setIsLoading(true);
            const response = await dispatch(handlePostRequest({ id: packageData._id }, "/deleteUnfinishedPackage", true, true));

            if (response !== "error") {
                toast.success("Package deleted successfully");
                fetchPendingPackages();
            }
        } catch (error) {
            console.error("Delete error:", error);
            toast.error("Failed to delete package");
        } finally {
            setIsLoading(false);
        }
    };

    const handleConvertToPackage = async (packageData) => {
        if (!packageData) {
            toast.error("Please select a package");
            return;
        }

        try {
            setIsLoading(true);

            // Move to pending first
            await dispatch(handlePostRequest({ id: packageData._id }, "/shiftPendingPackage"));

            // Get latest package info
            const lastPackage = await handleGetRequest("/getLastPackage");
            const lastWaybill = lastPackage?.data?.Waybill || 1000;
            const lastFlight = lastPackage?.data?.flightNumber || "UNKNOWN";

            // Format current date
            const currentDate = new Date();
            const formattedDate = `${currentDate.getDate().toString().padStart(2, "0")}-${(currentDate.getMonth() + 1).toString().padStart(2, "0")}-${currentDate.getFullYear()}`;

            // Prepare new package data
            const newPackage = {
                ...packageData,
                Waybill: lastWaybill + 1,
                flightNumber: lastFlight,
                shifted: true,
                name: packageData.billTo,
                billTo: packageData.packageName,
                numberOfPieces: packageData.quantity,
                courierName: packageData.courier,
                shipmentMethod: "air",
                number: 1,
                unit: "lb",
                trackingNumber: packageData.trackingNumber,
                date: formattedDate,
                oversizePackage: false,
                consigneeName: packageData.packageName,
                packageType: "nonperishable",
                deliveryType: "driver",
                _id: undefined,
                deliveryInfo: packageData.deliveryInfo,
                consigneeType: packageData.consigneeType ,
            };

            // Create the new package
            const response = await dispatch(handlePostRequest(newPackage, "/addPackage", true, true));

            if (response !== "error") {
                toast.success("Package converted successfully");
                fetchPendingPackages();
            }
        } catch (error) {
            console.error("Conversion error:", error);
            toast.error("Failed to convert package");
        } finally {
            setIsLoading(false);
        }
    };

    const generateBarcode = (trackingNumber) => {
        if (!trackingNumber) return null;

        const canvas = document.createElement("canvas");
        JsBarcode(canvas, trackingNumber, {
            format: "CODE128",
            displayValue: false,
        });

        return <img src={canvas.toDataURL()} alt={trackingNumber} style={{ width: "auto", height: "70px" }} />;
    };

    const renderActions = (rowData) => (
        <div className={styles.actionButtons}>
            {!isClerk && (
                <>
                    <Tooltip title="Edit" position="top" arrow>
                        <Button
                            icon="pi pi-pencil"
                            className="p-button-info"
                            onClick={() => {
                                setEditingPackage(rowData);
                                setIsDialogOpen(true);
                            }}
                        />
                    </Tooltip>

                    <Tooltip title="Delete" position="top" arrow>
                        <Button icon="pi pi-trash" className="p-button-danger" onClick={() => handleDeletePackage(rowData)} />
                    </Tooltip>
                </>
            )}

            {isAdmin && (
                <Tooltip title="Convert to package" position="top" arrow>
                    <Button icon="pi pi-sync" className="p-button-info p-mr-2" onClick={() => handleConvertToPackage(rowData)} />
                </Tooltip>
            )}
        </div>
    );

    return (
        <div className={styles.container}>
            <h2>Pending Packages</h2>
            {isClerk && <p style={{ color: "#666" }}>View only mode</p>}

            <div className={styles.buttonsWrapper}>
                {!isClerk && (
                    <Tooltip title="Add Manually" position="top" arrow>
                        <Button icon="pi pi-plus" className="p-button-success p-mr-2" onClick={() => setIsDialogOpen(true)} />
                    </Tooltip>
                )}

                {!isClerk && (isAdmin || userRole === "courier") && (
                    <Tooltip title="Upload CSV" position="top" arrow>
                        <Button icon="pi pi-upload" className="p-button-info p-mr-2" onClick={handleUploadCSV} />
                    </Tooltip>
                )}
            </div>

            <Dialog
                visible={isDialogOpen}
                onHide={() => {
                    setIsDialogOpen(false);
                    setEditingPackage(null);
                }}
                header={editingPackage ? "Edit Package" : "Add Package"}
                style={{ width: "50vw" }}
            >
                <AddSingleDialog onSubmit={handlePackageSubmit} editData={editingPackage} />
            </Dialog>

            {isLoading && (
                <div className={styles.spinnerWrapper}>
                    <ProgressSpinner />
                </div>
            )}

            <div className={styles.tableWrapper}>
                <DataTable
                    value={pendingPackages}
                    paginator
                    rows={100}
                    stripedRows
                    responsiveLayout="scroll"
                    rowsPerPageOptions={[100, 500, 1000]}
                    className="datatable-responsive"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Records"
                    emptyMessage="No packages found"
                    selection={selectedRow}
                    onSelectionChange={(e) => setSelectedRow(e.value)}
                    sortMode="multiple"
                    filterDisplay="row"
                    filterPlaceholder="Search"
                    filterLocale="en"
                >
                    {!isClerk && <Column selectionMode="multiple" style={{ width: "3em" }} />}
                    <Column header="Actions" body={renderActions} />
                    <Column field="description" header="Description" filter sortable />
                    <Column field="quantity" header="Quantity" filter sortable />
                    <Column field="packageName" header="Package Name" filter sortable />
                    <Column field="consigneeType" header="Client Type" filter sortable />
                    <Column field="courierName" header="Courier" filter sortable />
                    <Column field="billToName" header="Bill To" filter sortable />
                    <Column field="trackingNumber" header="Tracking #" filter sortable />
                    <Column field="arrivalTime" header="Est. arrival date" filter sortable />
                    <Column header="Barcode" body={(row) => generateBarcode(row.trackingNumber)} />
                </DataTable>
            </div>
        </div>
    );
};

export default PendingPackages;
